import React, {
  type ReactElement,
  useState,
  useEffect,
  useLayoutEffect
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  getProductTrialMetadata,
  type HttpError,
  type TrialMetadata
} from '../../common/api-client';
import billingClient from '../../common/utils/billing-client/client-v1';
import billingClientV2 from '../../common/utils/billing-client/client-v2';
import {
  type UserWithKeycloakId,
  type AxePurchaseState,
  type v2
} from '@deque/billing-service-client';
import { type Subscription, formatPrices } from '@deque/billing-utils';
import { Loader, Toast } from '@deque/cauldron-react';
import { useAuthContext } from '../../common/contexts/auth';
import { useEnterprises } from '../../common/contexts/enterprises';
import PlanView from '../pages/PlanView';
import getPrices from '../../common/utils/get-prices';
import ScrimmedLoader from '../../common/components/ScrimmedLoader';
import sendPlanChange from '../../common/utils/send-plan-change-to-extension';
import useDowngrade from '../../common/hooks/useDowngrade';
import DowngradeDialog from '../../common/components/DowngradeDialog';
import PageTitle from '../../common/components/PageTitle';
import { useAxeDevtoolsProAnalytics } from '../../common/contexts/analytics';
import getStatusMessage from '../../app/utils/get-status-message';
import { ProductSlugs } from '../../common/constants';
import { getTrialEligibility } from '@deque/billing-utils';
import { useFeatureFlagState } from '../../common/contexts/featureFlags';
import { useServerInfo } from '../../common/contexts/serverInfo';
import { useLocation } from 'react-router-dom';

const Plan = (): ReactElement => {
  const { t } = useTranslation();
  const downgradeState = useDowngrade();
  const [loading, setLoading] = useState(true);
  const [affirming, setAffirming] = useState(false);
  const [needsPlan, setNeedsPlan] = useState<boolean>(false);
  const [enterpriseNameForNonAdmin, setEnterpriseNameForNonAdmin] =
    useState<string>();
  const [billingUser, setBillingUser] = useState<UserWithKeycloakId>();
  const [unFilteredProducts, setUnFilteredProducts] = useState<v2.Product[]>();
  const [filteredProducts, setFilteredProducts] = useState<v2.Product[]>();
  const [axeProSubscription, setAxeProSubscription] = useState<Subscription>();
  const [bundlePurchaseState, setBundlePurchaseState] =
    React.useState<AxePurchaseState>('none');
  const [statusMessage, setStatusMessage] = useState('');
  const [statusVariant, setStatusVariant] = useState<
    'success' | 'warn' | 'alert'
  >('warn');
  const [priceAmount, setPriceAmount] = useState('');
  const [term, setTerm] = useState('month');
  const [trialMetadata, setTrialMetadata] = useState<TrialMetadata>();
  const {
    checkForUser,
    user,
    login,
    billingUser: v2BillingUser,
    updateBillingUser
  } = useAuthContext();
  const [axeDevToolsExtension, setAxeDevToolsExtension] =
    useState<v2.Product | null>(null);
  const { activeEnterprise, isAdmin } = useEnterprises();
  const allowRepeatTrials = useFeatureFlagState('allow_repeat_trials');
  const loaderRef = React.createRef<HTMLDivElement>();
  const alertPortal = React.createRef<HTMLDivElement>();
  const axeProPurchaseState = (axeProSubscription?.purchase_state ||
    'none') as AxePurchaseState;
  const isAxeProOnline = !!axeProSubscription?.stripe_id;

  const analytics = useAxeDevtoolsProAnalytics();
  const { serverInfo } = useServerInfo();
  const location = useLocation();

  useEffect(() => {
    analytics.viewPlansPage(window.location.href);
  }, []);

  useEffect(() => {
    loaderRef.current?.focus();
  }, [loaderRef.current]);

  // pre mount - add the body modifier class (to cancel out the Workspace
  // offset since plans need to render the footer component at 100% width)
  // NOTE: on unmount the class will be removed
  useLayoutEffect(() => {
    document.body.classList.add('NoMainOffset');
    return () => document.body.classList.remove('NoMainOffset');
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);

    if (searchParams.get('needsActivePlan')) {
      setNeedsPlan(true);
    }
  }, [location]);

  useEffect(() => {
    const getProductData = async () => {
      sendPlanChange();
      const [trialData, prods] = await Promise.all([
        getProductTrialMetadata(ProductSlugs.axeDevToolsExtension),
        billingClientV2.products.list(),
        checkForUser()
      ]);
      setUnFilteredProducts(prods);
      setTrialMetadata(trialData);
      const axeDevToolsExtensionProduct = prods.find(
        p => p.slug === ProductSlugs.axeDevToolsExtension
      );
      if (axeDevToolsExtensionProduct) {
        setAxeDevToolsExtension(axeDevToolsExtensionProduct);
      }
      setLoading(false);
    };
    getProductData();
  }, []);

  useEffect(() => {
    if (!unFilteredProducts) {
      return;
    }
    if (serverInfo?.isSelfProvisioningEnabled) {
      const fp = unFilteredProducts.filter(
        p => !!p?.stripe_data?.pricing?.length
      );
      setFilteredProducts(fp);
    } else {
      // If self provisioning is not enabled we don't need to filter products on stripe prices
      setFilteredProducts(unFilteredProducts);
    }
  }, [unFilteredProducts, serverInfo]);

  useEffect(() => {
    if (enterpriseNameForNonAdmin) {
      return;
    }
    const customerPrice = axeProSubscription?.pricing;
    let amount, interval;
    if (customerPrice) {
      amount = customerPrice.amount;
      interval = customerPrice.interval;
    } else {
      const { monthlyPrice, yearlyPrice } = getPrices(filteredProducts);
      const price = monthlyPrice || yearlyPrice;
      amount = price && price.unit_amount;
      interval = price && price.recurring && price.recurring.interval;
    }

    const unitAmount = formatPrices(amount && amount / 100);

    setPriceAmount(unitAmount || '');
    setTerm(
      interval === 'month' || interval === 'monthly' ? t('month') : t('year')
    );
  }, [enterpriseNameForNonAdmin, filteredProducts, axeProSubscription]);

  useEffect(() => {
    const getUserData = async () => {
      if (!user || !user.token || billingUser) {
        return;
      }
      setLoading(true);
      try {
        const bu = await billingClient.me.get(user.token);
        setBillingUser(bu);
      } catch (err) {
        if ((err as HttpError).status === 401) {
          // should not happen because we can only get here if we are logged in
          login();
        }
      }
      setLoading(false);
    };
    getUserData();
  }, [user, enterpriseNameForNonAdmin, billingUser]);

  const updateBillingUserV1 = async () => {
    if (user && user.token) {
      const bu = await billingClient.me.get(user.token);
      setBillingUser(bu);
    }
  };

  useEffect(() => {
    if (!billingUser || !v2BillingUser) {
      return;
    }

    const subscriptions = activeEnterprise
      ? activeEnterprise.subscriptions
      : v2BillingUser.subscriptions;

    const proSubscription = (subscriptions as Subscription[]).find(
      s => s.product_slug === ProductSlugs.axeDevToolsExtension
    );
    const htmlSubscription = (subscriptions as Subscription[]).find(
      s => s.product_slug === ProductSlugs.axeDevToolsHTML
    );

    setAxeProSubscription(proSubscription);
    setBundlePurchaseState(
      htmlSubscription?.purchase_state as AxePurchaseState
    );

    if (activeEnterprise && !isAdmin) {
      setEnterpriseNameForNonAdmin(activeEnterprise.name);
      if (proSubscription) {
        const { statusMessage: message, statusMessageVariant } =
          getStatusMessage({
            subscription: proSubscription,
            t,
            activeEnterprise,
            isAdmin
          });

        setStatusMessage(message);
        setStatusVariant(statusMessageVariant);
      }

      return;
    }

    if (proSubscription) {
      const { statusMessage: message, statusMessageVariant } = getStatusMessage(
        {
          subscription: proSubscription,
          t,
          activeEnterprise,
          isAdmin
        }
      );

      setStatusMessage(message);
      setStatusVariant(statusMessageVariant);
    }
  }, [billingUser, v2BillingUser, activeEnterprise, isAdmin]);

  const handleConfirmDowngradeClick = async () => {
    /* istanbul ignore next */
    if (!user || !!enterpriseNameForNonAdmin || !v2BillingUser) {
      return;
    }

    // close the modal
    setAffirming(false);

    // online or trial_ended Pro is the only subscription that is downgradable
    if (
      isAxeProOnline ||
      (axeProSubscription && axeProPurchaseState === 'trial_ended')
    ) {
      await downgradeState.downgrade({
        subscription: axeProSubscription,
        user
      });
    }
    setLoading(true);
    await updateBillingUser();
    await updateBillingUserV1();
    setLoading(false);
  };

  const handleDowngradeClick = () => {
    /* istanbul ignore if */
    if (enterpriseNameForNonAdmin) {
      // belt and suspenders
      return;
    }
    analytics.downgrade();
    setAffirming(true);
  };

  const handleCancel = () => {
    setAffirming(false);
  };

  let canUserRestartTrial = false;
  // Make sure that we have variable that we need to check if restarting trial is allowed
  if (allowRepeatTrials && v2BillingUser && axeDevToolsExtension) {
    const trialEligibility = getTrialEligibility({
      user: v2BillingUser,
      product: axeDevToolsExtension,
      enterprise: activeEnterprise ? activeEnterprise : undefined,
      isEnterpriseAdmin: isAdmin
    });

    canUserRestartTrial =
      trialEligibility.canStartTrial && trialEligibility.isRestart;
  }

  return (
    <div>
      <PageTitle title={t('Plans')} />
      {loading || downgradeState.loading ? (
        <Loader label={t('Loading')} tabIndex={-1} ref={loaderRef} />
      ) : (
        <div>
          {axeProSubscription && (
            <DowngradeDialog
              show={affirming}
              onClose={handleCancel}
              onConfirm={handleConfirmDowngradeClick}
              isPaidPlan={axeProSubscription?.purchase_state === 'paid'}
              subscription={axeProSubscription}
            />
          )}
          <div ref={alertPortal}>
            {needsPlan && (
              <Toast show type="caution">
                {t('You must upgrade to use this feature.')}
              </Toast>
            )}
            {downgradeState.error && (
              <Toast show type="caution">
                {downgradeState.error}
              </Toast>
            )}
            {downgradeState.loading && (
              <ScrimmedLoader
                label={t('Downgrading...')}
                loaderRef={loaderRef}
              />
            )}
            {filteredProducts && (
              <PlanView
                state={axeProPurchaseState}
                canUserRestartTrial={canUserRestartTrial}
                isAxeProOnline={isAxeProOnline}
                enterpriseNameForNonAdmin={enterpriseNameForNonAdmin}
                isPendingCancellation={!!axeProSubscription?.cancel_at}
                statusMessage={
                  axeProPurchaseState !== 'free' ? statusMessage : ''
                }
                statusVariant={statusVariant}
                priceAmount={priceAmount}
                term={term}
                filteredProducts={filteredProducts}
                handleDowngradeClick={handleDowngradeClick}
                trialDuration={trialMetadata?.duration}
                bundlePurchaseState={bundlePurchaseState}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Plan;
